import * as React from "react";
import * as Immutable from "immutable";
import { useContext, useCallback } from "react";
import { Formik, Form, Field } from "formik";
import styled from "styled-components";
import useProductName from "brand-customization/useProductName";
import { InputOptionalInfo as Opt, FormikFormGroup, Select } from "components/common";
import { Button, ButtonToolbar, Alert, Row, Col, Input } from "components/bootstrap";
import BackendWizardContext from "components/authentication/directoryServices/BackendWizard/BackendWizardContext";
import { validateField, formHasErrors } from "util/FormsUtils";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import MatchingGroupsOverview from "./MatchingGroupsOverview";
import SelectedGroupsOverview from "./SelectedGroupsOverview";
import SelectionTypeOptions from "./SelectionTypeOptions";
import GroupSyncCheckbox from "./GroupSyncCheckbox";
import LicenseCheck from "../../../license/LicenseCheck";
const Headline = styled.h3`
  margin-bottom: 20px;
`;
export const formValidation = (selectionType) => {
  let teamSelection;
  if (selectionType !== "all") {
    teamSelection = {
      required: true
    };
  }
  const validation = {
    groupSearchBase: {
      required: true
    },
    groupSearchPattern: {
      required: true
    },
    teamNameAttribute: {
      required: true
    },
    teamUniqueIdAttribute: {
      required: true
    },
    teamDefaultRoles: {},
    teamSelectionType: {},
    teamSelection
  };
  return validation;
};
const _getInitialValues = (formValues) => {
  const {
    groupSearchPattern,
    groupSearchBase,
    teamUniqueIdAttribute,
    teamNameAttribute,
    teamDefaultRoles,
    teamSelectionType,
    teamSelection,
    synchronizeGroups
  } = formValues;
  return {
    synchronizeGroups,
    groupSearchPattern,
    groupSearchBase,
    teamUniqueIdAttribute,
    teamNameAttribute,
    teamDefaultRoles,
    teamSelectionType: teamSelectionType || "all",
    teamSelection: teamSelection || Immutable.Set()
  };
};
const GroupSyncForm = ({
  help = {},
  prepareSubmitPayload,
  formRef,
  onSubmitAll,
  submitAllError,
  validateOnMount,
  roles,
  excludedFields
}) => {
  const { setStepsState, ...stepsState } = useContext(BackendWizardContext);
  const { backendValidationErrors } = stepsState;
  const rolesOptions = roles.map((role) => ({ label: role.name, value: role.id })).toArray();
  const _formValidation = useCallback((teamSelectionType) => formValidation(teamSelectionType), []);
  const sendTelemetry = useSendTelemetry("services-groupsync-step");
  const productName = useProductName();
  const _onSubmitAll = (validateForm, values, licenseIsValid) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.DIRECTORY_GROUP_SYNC_SAVE_CLICKED, {
      app_action_value: "save-service"
    });
    if (values.synchronizeGroups) {
      return validateForm().then((errors) => {
        if (!formHasErrors(errors)) {
          onSubmitAll(licenseIsValid);
        }
      });
    }
    return onSubmitAll(licenseIsValid);
  };
  return /* @__PURE__ */ React.createElement(LicenseCheck, null, ({ licenseIsValid }) => {
    const disableGroupSyncStep = !licenseIsValid;
    return /* @__PURE__ */ React.createElement(
      Formik,
      {
        initialValues: _getInitialValues(stepsState.formValues),
        onSubmit: () => onSubmitAll(licenseIsValid),
        innerRef: formRef,
        validateOnBlur: false,
        validateOnChange: false,
        validateOnMount
      },
      ({ isSubmitting, values, setFieldValue, validateForm }) => {
        const validation = _formValidation(values.teamSelectionType);
        const disableForm = disableGroupSyncStep || !values.synchronizeGroups;
        const _onGroupSelect = (groupId) => {
          var _a;
          const selectedGroupsIds = (_a = values.teamSelection) != null ? _a : Immutable.List();
          if (selectedGroupsIds == null ? void 0 : selectedGroupsIds.includes(groupId)) {
            setFieldValue("teamSelection", selectedGroupsIds.remove(groupId));
          } else {
            setFieldValue("teamSelection", selectedGroupsIds.add(groupId));
          }
        };
        return /* @__PURE__ */ React.createElement(Form, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { xs: 12 }, /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info", title: "Introduction" }, "This step enables importing ", stepsState.authBackendMeta.serviceTitle, " groups as ", productName, " ", "teams. The group synchronization can be activated by clicking on the checkbox with the label", " ", /* @__PURE__ */ React.createElement("i", null, "Enable Group Synchronization"), ". Afterwards fill out the form and test your settings in the second section ", /* @__PURE__ */ React.createElement("i", null, "Load matching groups"), ". The last section ", /* @__PURE__ */ React.createElement("i", null, "Select groups to import"), " ", "allows you to specify which groups you want to import."))), /* @__PURE__ */ React.createElement(GroupSyncCheckbox, { disabled: disableGroupSyncStep }), /* @__PURE__ */ React.createElement("hr", null), /* @__PURE__ */ React.createElement(
          FormikFormGroup,
          {
            help: help.groupSearchBase,
            validate: validateField(validation.groupSearchBase),
            error: backendValidationErrors == null ? void 0 : backendValidationErrors.groupSearchBase,
            label: "Group Search Base DN",
            disabled: disableForm,
            name: "groupSearchBase",
            placeholder: "Group Search Base"
          }
        ), /* @__PURE__ */ React.createElement(
          FormikFormGroup,
          {
            help: help.groupSearchPattern,
            validate: validateField(validation.groupSearchPattern),
            error: backendValidationErrors == null ? void 0 : backendValidationErrors.groupSearchPattern,
            disabled: disableForm,
            label: "Group Search Pattern",
            name: "groupSearchPattern",
            placeholder: "Group Search Pattern"
          }
        ), /* @__PURE__ */ React.createElement(
          FormikFormGroup,
          {
            help: help.teamNameAttribute,
            validate: validateField(validation.teamNameAttribute),
            error: backendValidationErrors == null ? void 0 : backendValidationErrors.teamNameAttribute,
            disabled: disableForm,
            label: "Team Name Attribute",
            name: "teamNameAttribute",
            placeholder: "Team Name Attribute"
          }
        ), !excludedFields.teamUniqueIdAttribute && /* @__PURE__ */ React.createElement(
          FormikFormGroup,
          {
            help: help.teamUniqueIdAttribute,
            label: "Team ID Attribute",
            error: backendValidationErrors == null ? void 0 : backendValidationErrors.teamUniqueIdAttribute,
            validate: validateField(validation.teamUniqueIdAttribute),
            disabled: disableForm,
            name: "teamUniqueIdAttribute",
            placeholder: "Team ID Attribute"
          }
        ), /* @__PURE__ */ React.createElement(Field, { name: "teamDefaultRoles", validate: validateField(validation.teamDefaultRoles) }, ({ field: { name, value, onChange, onBlur }, meta: { error } }) => /* @__PURE__ */ React.createElement(
          Input,
          {
            bsStyle: error ? "error" : void 0,
            help: help.teamDefaultRoles,
            error: error != null ? error : backendValidationErrors == null ? void 0 : backendValidationErrors.teamDefaultRoles,
            id: "default-team-roles-select",
            disabled: disableForm,
            label: /* @__PURE__ */ React.createElement(React.Fragment, null, "Default Team Roles ", /* @__PURE__ */ React.createElement(Opt, null)),
            labelClassName: "col-sm-3",
            wrapperClassName: "col-sm-9"
          },
          /* @__PURE__ */ React.createElement(
            Select,
            {
              multi: true,
              onBlur,
              onChange: (selectedRoles) => onChange({ target: { value: selectedRoles, name } }),
              options: rolesOptions,
              disabled: disableForm,
              placeholder: "Search for roles",
              value
            }
          )
        )), /* @__PURE__ */ React.createElement(Headline, null, "Load matching groups"), /* @__PURE__ */ React.createElement(
          MatchingGroupsOverview,
          {
            disabled: disableForm,
            prepareSubmitPayload,
            onGroupSelect: _onGroupSelect
          }
        ), /* @__PURE__ */ React.createElement(Headline, null, "Select groups to import"), /* @__PURE__ */ React.createElement(SelectionTypeOptions, { disabled: disableForm }), /* @__PURE__ */ React.createElement(SelectedGroupsOverview, { onGroupSelect: _onGroupSelect, validation }), submitAllError, /* @__PURE__ */ React.createElement(ButtonToolbar, { className: "pull-right" }, /* @__PURE__ */ React.createElement(
          Button,
          {
            bsStyle: "primary",
            disabled: isSubmitting,
            onClick: () => _onSubmitAll(validateForm, values, licenseIsValid)
          },
          "Finish & Save Service"
        )));
      }
    );
  });
};
export default GroupSyncForm;
